import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { Zap, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface PerformanceMetric {
  name: string;
  current: number;
  optimal: number;
  unit: string;
  status: 'good' | 'warning' | 'critical';
}

export const PerformanceOptimizationPanel = () => {
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([
    { name: 'Database Query Time', current: 45, optimal: 50, unit: 'ms', status: 'good' },
    { name: 'API Response Time', current: 120, optimal: 200, unit: 'ms', status: 'good' },
    { name: 'Page Load Time', current: 1.8, optimal: 3, unit: 's', status: 'good' },
    { name: 'Memory Usage', current: 68, optimal: 80, unit: '%', status: 'good' },
    { name: 'CPU Usage', current: 45, optimal: 70, unit: '%', status: 'good' },
    { name: 'Cache Hit Rate', current: 87, optimal: 90, unit: '%', status: 'warning' },
  ]);

  const [optimizing, setOptimizing] = useState(false);

  const runOptimization = async () => {
    setOptimizing(true);
    toast.info('Running performance optimization...');

    // Simulate optimization process
    for (let i = 0; i < 3; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setMetrics(prev => prev.map(metric => ({
        ...metric,
        current: Math.max(
          metric.current * 0.9,
          metric.optimal * 0.7
        ),
        status: metric.current <= metric.optimal * 0.8 ? 'good' : 
                metric.current <= metric.optimal ? 'warning' : 'critical'
      })));
    }

    setOptimizing(false);
    toast.success('Performance optimization complete!');
  };

  const getPerformanceScore = () => {
    const scores = metrics.map(m => 
      Math.min(100, (m.optimal / Math.max(m.current, 1)) * 100)
    );
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  const score = getPerformanceScore();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Performance Optimization
        </CardTitle>
        <CardDescription>
          Monitor and optimize system performance metrics
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Overall Performance Score</p>
            <p className="text-3xl font-bold mt-1">{score}%</p>
            <p className="text-xs text-muted-foreground mt-1">
              {score >= 90 ? 'Excellent' : score >= 75 ? 'Good' : score >= 60 ? 'Fair' : 'Needs Improvement'}
            </p>
          </div>

          <Button
            onClick={runOptimization}
            disabled={optimizing}
            size="lg"
          >
            <Activity className={`h-4 w-4 mr-2 ${optimizing ? 'animate-spin' : ''}`} />
            {optimizing ? 'Optimizing...' : 'Optimize System'}
          </Button>
        </div>

        <Progress value={score} className="h-2" />

        <div className="grid gap-4">
          {metrics.map((metric, index) => {
            const percentage = (metric.current / metric.optimal) * 100;
            const isBelowOptimal = metric.current <= metric.optimal;

            return (
              <div
                key={index}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{metric.name}</span>
                    <div className="flex items-center gap-2">
                      {isBelowOptimal ? (
                        <TrendingDown className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingUp className="h-4 w-4 text-red-600" />
                      )}
                      <Badge
                        variant={
                          metric.status === 'good' ? 'default' :
                          metric.status === 'warning' ? 'secondary' :
                          'destructive'
                        }
                      >
                        {metric.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">
                      Current: <strong>{metric.current.toFixed(1)}{metric.unit}</strong>
                    </span>
                    <span className="text-muted-foreground">
                      Optimal: <strong>{metric.optimal}{metric.unit}</strong>
                    </span>
                  </div>

                  <Progress
                    value={Math.min(percentage, 100)}
                    className="h-1 mt-2"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Optimization Recommendations</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Enable database query caching for frequently accessed data</li>
            <li>• Implement CDN for static assets</li>
            <li>• Use lazy loading for large content modules</li>
            <li>• Optimize images and compress resources</li>
            <li>• Enable HTTP/2 and compression</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
